import { useState, useEffect } from 'react';

interface TimePickerProps {
  value: string; // HH:MM format (24-hour)
  onChange: (value: string) => void;
  className?: string;
}

export function TimePicker({ value, onChange, className = '' }: TimePickerProps) {
  const [hour, setHour] = useState('09');
  const [minute, setMinute] = useState('00');
  const [period, setPeriod] = useState<'AM' | 'PM'>('AM');

  // Parse the 24-hour value into 12-hour format on mount/change
  useEffect(() => {
    const [h, m] = value.split(':');
    const hourNum = parseInt(h, 10);

    setMinute(m);

    if (hourNum === 0) {
      setHour('12');
      setPeriod('AM');
    } else if (hourNum < 12) {
      setHour(hourNum.toString().padStart(2, '0'));
      setPeriod('AM');
    } else if (hourNum === 12) {
      setHour('12');
      setPeriod('PM');
    } else {
      setHour((hourNum - 12).toString().padStart(2, '0'));
      setPeriod('PM');
    }
  }, [value]);

  // Convert 12-hour format back to 24-hour and notify parent
  function updateTime(newHour: string, newMinute: string, newPeriod: 'AM' | 'PM') {
    let hour24 = parseInt(newHour, 10);

    if (newPeriod === 'AM' && hour24 === 12) {
      hour24 = 0;
    } else if (newPeriod === 'PM' && hour24 !== 12) {
      hour24 += 12;
    }

    const time24 = `${hour24.toString().padStart(2, '0')}:${newMinute}`;
    onChange(time24);
  }

  function handleHourChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const newHour = e.target.value;
    setHour(newHour);
    updateTime(newHour, minute, period);
  }

  function handleMinuteChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const newMinute = e.target.value;
    setMinute(newMinute);
    updateTime(hour, newMinute, period);
  }

  function handlePeriodChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const newPeriod = e.target.value as 'AM' | 'PM';
    setPeriod(newPeriod);
    updateTime(hour, minute, newPeriod);
  }

  const selectClass = "px-3 py-2 rounded-lg border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-800 text-navy-800 dark:text-surface-100 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 cursor-pointer";

  return (
    <div className={`flex gap-2 ${className}`}>
      {/* Hour */}
      <select
        value={hour}
        onChange={handleHourChange}
        className={selectClass}
        aria-label="Hour"
      >
        {Array.from({ length: 12 }, (_, i) => {
          const h = (i + 1).toString().padStart(2, '0');
          return (
            <option key={h} value={h}>
              {h}
            </option>
          );
        })}
      </select>

      <span className="flex items-center text-navy-800 dark:text-surface-100 font-medium">:</span>

      {/* Minute */}
      <select
        value={minute}
        onChange={handleMinuteChange}
        className={selectClass}
        aria-label="Minute"
      >
        {Array.from({ length: 60 }, (_, i) => {
          const m = i.toString().padStart(2, '0');
          return (
            <option key={m} value={m}>
              {m}
            </option>
          );
        })}
      </select>

      {/* AM/PM */}
      <select
        value={period}
        onChange={handlePeriodChange}
        className={selectClass}
        aria-label="AM or PM"
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
}
